import React, { useEffect, useRef, useState } from 'react';
import { Landmark, Copy, Check, MessageCircle } from 'lucide-react';
import { CLINIC } from '../mock';
import { slotLabel } from './SlotPicker';

const CopyRow = ({ label, value, strong }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setDone(true); setTimeout(() => setDone(false), 1500); } catch { /* clipboard blocked */ }
  };
  const id = label.toLowerCase().replace(/[^a-z]+/g, '-');
  return (
    <div className="flex items-start justify-between gap-3 py-2.5 border-b border-teal-50 last:border-0">
      <div className="min-w-0">
        <div className="text-xs text-slate-500">{label}</div>
        <div data-testid={`bank-${id}`} className={`break-words ${strong ? 'font-semibold text-teal-900' : 'text-slate-800'}`}>{value}</div>
      </div>
      <button type="button" data-testid={`bank-copy-${id}`} onClick={copy} aria-label={`Copy ${label}`} className="shrink-0 grid place-items-center w-8 h-8 rounded-full hover:bg-teal-50 text-teal-700">
        {done ? <Check size={15} /> : <Copy size={15} />}
      </button>
    </div>
  );
};

export const BankDetails = ({ data, slot, form }) => {
  const top = useRef(null);
  useEffect(() => { top.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, []);
  const when = slotLabel(slot.date, slot.time);
  const msg = encodeURIComponent(`Hello, I have booked ${when} IST and am sending USD ${data.amount_usd} by bank transfer (reference ${data.reference}). Name: ${form.name}, ${form.country}. Attaching my transfer receipt.`);
  return (
    <div ref={top} data-testid="bank-transfer-details" className="space-y-5 scroll-mt-28 pb-16 sm:pb-0">
      <div className="text-center">
        <span className="grid place-items-center w-14 h-14 rounded-full bg-teal-50 text-teal-600 mx-auto"><Landmark size={28} /></span>
        <h3 className="font-display text-2xl text-teal-900 mt-3" style={{ fontWeight: 600 }}>Your slot is held</h3>
        <p data-testid="bank-slot" className="inline-block mt-2 px-4 py-2 rounded-full bg-teal-50 text-teal-800 font-semibold text-sm">{when} IST</p>
      </div>
      <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900">
        Please send <strong data-testid="bank-amount">USD {data.amount_usd} (about GBP {data.gbp_estimate})</strong> and write <strong data-testid="bank-reference-note">{data.reference}</strong> as the payment reference. GBP amount is an estimate; you can also send USD to the IBAN by Swift.
      </div>
      <div className="rounded-2xl border border-teal-100 px-4">
        {Object.entries(data.bank).map(([k, v]) => <CopyRow key={k} label={k} value={v} strong={k !== 'Bank'} />)}
        <CopyRow label="Reference" value={data.reference} strong />
      </div>
      <p className="text-xs text-slate-500">Sort code is for UK transfers. From outside the UK, use the IBAN and Swift/BIC. Your booking is confirmed once our team sees the payment.</p>
      <a data-testid="bank-send-receipt" href={`${CLINIC.whatsapp}?text=${msg}`} target="_blank" rel="noreferrer" className="w-full inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full font-semibold text-white" style={{ background: '#25D366' }}>
        <MessageCircle size={18} /> Send transfer receipt on WhatsApp
      </a>
    </div>
  );
};

export const MethodPicker = ({ value, onChange }) => (
  <div data-testid="pay-method-picker" className="grid grid-cols-2 gap-2">
    {[['card', 'Card payment'], ['wise', 'Bank transfer (Wise)']].map(([id, label]) => (
      <button key={id} type="button" data-testid={`pay-method-${id}`} onClick={() => onChange(id)}
        className={`px-4 py-3 rounded-xl border text-sm font-semibold transition-colors ${value === id ? 'border-teal-500 bg-teal-50 text-teal-900' : 'border-teal-100 bg-white text-slate-600 hover:border-teal-300'}`}>{label}</button>
    ))}
  </div>
);
